import { useMemo, useRef } from "react"

/**
 * A helper hook for keeping a reference to a callback function that may change over time.
 *
 * Useful for wrapping around a prop callback function, which you might want to use in a useEffect, without needing to add that function to the dependency array.
 *
 * If the callback is undefined, then the returned function will be a no-op.
 */
export function useCallbackRef<T extends ((...args: any) => any) | undefined>(func: T) {
  const ref = useRef(func)
  ref.current = func

  return useMemo(
    () =>
      (...args: T extends (...args: infer A) => any ? A : never): T extends (...args: any) => infer R ? R : void => {
        return ref.current?.(...args)
      },
    [],
  )
}
